import React from "react";

import { resolveAnswerLink, AnswerSource } from "../utils/answerLinks";

/**
 * A link inside a Copilot (beta) answer. Split out of CopilotTwo.tsx so it can be tested:
 * CopilotTwo imports react-markdown, which is ESM-only and does not resolve under react-scripts'
 * Jest (the same reason CitationTwo.tsx is its own file).
 *
 * ACOP-152 test finding, AC-12 — see utils/answerLinks.ts for the full story. In short: a link
 * written as `[Title](#4)` opens source 4 in the citation panel like the numbered pill does; a link
 * with nowhere to go is plain text; every other link is the same anchor it always was.
 */

type Props = {
  href?: string;
  sources?: AnswerSource[];
  onCitationClick?: (source: AnswerSource) => void;
  onLinkClick?: (href: string | undefined) => void;
  children?: React.ReactNode;
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "children">;

export default function AnswerLink({ href, sources, onCitationClick, onLinkClick, children, ...rest }: Props) {
  const target = resolveAnswerLink(href, sources);

  if (target.kind === "citation") {
    // Neither an anchor (there is no address to put in an href, and `href="#4"` is the bug) nor a
    // button (a button is always inline-block, so a long title would not wrap with the sentence and
    // would take its list bullet with it). A focusable inline element with the link role reads, wraps
    // and tabs like the other answer links; Enter opens it, as it does a link.
    const open = () => onCitationClick?.(target.source);
    return (
      <span
        role="link"
        tabIndex={0}
        className="c2-md-link c2-md-link--citation"
        title={target.source.title}
        data-testid="c2-answer-source-link"
        data-id={target.source.id}
        onClick={open}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); open(); } }}
      >
        {children}
      </span>
    );
  }

  if (target.kind === "text") return <span>{children}</span>;

  return (
    <a
      href={target.href}
      className="c2-md-link"
      onClick={(e) => { e.preventDefault(); onLinkClick?.(target.href); }}
      {...rest}
    >
      {children}
    </a>
  );
}
